import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Download, Search, Filter } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtCur = (n) => `₹${fmt(n)}`;

export default function MarginCompareView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCustomer) params.set('customer', filterCustomer);
      if (filterSupplier) params.set('supplier', filterSupplier);
      if (onlyAlerts) params.set('only_alerts', 'true');
      const [dataRes, custRes, supRes] = await Promise.all([
        api.get(`/margin-compare?${params.toString()}`),
        api.get('/customers'),
        api.get('/suppliers'),
      ]);
      setRows(dataRes.data);
      setCustomers(custRes.data.map(c => c.name).sort());
      setSuppliers(supRes.data);
    } catch { toast.error('Failed to load margin data'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filterCustomer, filterSupplier, onlyAlerts]);

  const filtered = rows.filter(r =>
    !search || r.part_number?.toLowerCase().includes(search.toLowerCase()) || r.part_name?.toLowerCase().includes(search.toLowerCase())
  );

  const alertCount = rows.filter(r => r.alert_triggered).length;

  const handleExportCSV = () => {
    const headers = ['Part Number','Part Name','Customer','Customer Price','Best Supplier','Supplier Part #','Best PO','Margin','Margin %','Alert'];
    const csvRows = filtered.map(r => [
      r.part_number, r.part_name, r.customer_name, r.customer_price,
      r.best_supplier_name, r.supplier_part_number || '', r.best_supplier_po,
      r.margin, r.margin_pct, r.alert_triggered ? 'YES' : ''
    ]);
    const csv = [headers, ...csvRows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `margin_compare_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="space-y-4" data-testid="margin-compare-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-slate-500 text-sm">
          {filtered.length} traded part{filtered.length !== 1 ? 's' : ''}
          {alertCount > 0 && <span className="text-red-600 font-semibold ml-2">{alertCount} below threshold</span>}
        </p>
        <button onClick={handleExportCSV} disabled={filtered.length === 0} data-testid="export-csv-btn"
          className="flex items-center gap-2 px-3 h-8 border border-slate-300 rounded-sm text-xs font-semibold hover:bg-slate-50 disabled:opacity-40">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2 flex-1 min-w-48">
          <Search size={14} className="text-slate-400" />
          <input type="text" placeholder="Search part number or name..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="margin-search"
            className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
        </div>
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} data-testid="margin-filter-customer"
          className="border border-slate-300 rounded-sm h-8 px-2 text-sm bg-white">
          <option value="">All Customers</option>
          {customers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)} data-testid="margin-filter-supplier"
          className="border border-slate-300 rounded-sm h-8 px-2 text-sm bg-white">
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyAlerts} onChange={e => setOnlyAlerts(e.target.checked)}
            data-testid="only-alerts-toggle"
            className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
          <Filter size={13} className="text-red-500" /> Only Alerts
        </label>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full min-w-[1000px]">
              <thead><tr>
                <th className="text-left">Our Part #</th>
                <th className="text-left">Part Name</th>
                <th className="text-left">Customer</th>
                <th className="text-right">Customer Price</th>
                <th className="text-left">Best Supplier</th>
                <th className="text-left">Supplier Part #</th>
                <th className="text-right">Best PO</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Margin %</th>
                <th className="text-center">Alert</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="py-12 text-center text-slate-400">
                    No traded parts found. Add supplier costings to see margin data.
                  </td></tr>
                ) : filtered.map(r => (
                  <tr key={r.part_id} className={`group cursor-pointer ${r.alert_triggered ? 'bg-red-50/50' : ''}`}
                    onClick={() => setExpandedRow(expandedRow === r.part_id ? null : r.part_id)}>
                    <td className="font-mono font-semibold text-slate-800">{r.part_number}</td>
                    <td className="text-slate-700 text-sm max-w-[160px] truncate">{r.part_name}</td>
                    <td className="text-slate-600 text-xs">{r.customer_name}</td>
                    <td className="text-right font-mono text-sm text-slate-700">{fmtCur(r.customer_price)}</td>
                    <td className="text-slate-600 text-xs">{r.best_supplier_name}</td>
                    <td className="font-mono text-xs text-slate-500">{r.supplier_part_number || '—'}</td>
                    <td className="text-right font-mono text-sm font-semibold text-slate-800">{fmtCur(r.best_supplier_po)}</td>
                    <td className={`text-right font-mono text-sm font-semibold ${r.margin < 0 ? 'text-red-600' : 'text-slate-700'}`}>{fmtCur(r.margin)}</td>
                    <td className={`text-right font-mono text-sm font-bold ${r.alert_triggered ? 'text-red-600' : r.margin_pct >= 15 ? 'text-emerald-600' : 'text-amber-600'}`}>{r.margin_pct.toFixed(1)}%</td>
                    <td className="text-center">
                      {r.alert_triggered && <AlertTriangle size={14} className="text-red-500 mx-auto" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Expanded row detail */}
      {expandedRow && (() => {
        const r = filtered.find(x => x.part_id === expandedRow);
        if (!r) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-bold text-slate-800 mb-3">All suppliers for {r.part_number} — {r.part_name}</p>
            <div className="space-y-1">
              {(r.all_supplier_pos || []).map((s, i) => (
                <div key={i} className={`flex items-center justify-between py-1.5 px-3 rounded-sm text-sm ${i === 0 ? 'bg-emerald-50 font-semibold' : 'bg-slate-50'}`}>
                  <span className="text-slate-700">{s.supplier_name}</span>
                  <span className="font-mono text-slate-800">{fmtCur(s.po_price)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
